import { useState, useMemo } from 'react';
import { Link } from 'wouter';
import { ChevronDown } from 'lucide-react';
import type { TabContent } from '@/types/cms';

interface IndexTOCProps {
  folderSlug: string;
  tabs: TabContent[];
}

interface Group {
  category: string | null;
  tabs: TabContent[];
}

function groupByCategory(tabs: TabContent[]): Group[] {
  const ungrouped: TabContent[] = [];
  const buckets = new Map<string, TabContent[]>();
  const order: string[] = [];

  for (const t of tabs) {
    if (!t.category) {
      ungrouped.push(t);
      continue;
    }
    if (!buckets.has(t.category)) {
      buckets.set(t.category, []);
      order.push(t.category);
    }
    buckets.get(t.category)!.push(t);
  }

  const groups: Group[] = [];
  if (ungrouped.length > 0) groups.push({ category: null, tabs: ungrouped });
  for (const cat of order) groups.push({ category: cat, tabs: buckets.get(cat)! });
  return groups;
}

export function IndexTOC({ folderSlug, tabs }: IndexTOCProps) {
  const [isOpen, setIsOpen] = useState(false);
  const groups = useMemo(() => groupByCategory(tabs), [tabs]);
  const total = tabs.length;

  if (total === 0) return null;

  return (
    <nav className="border border-gray-200/80 rounded-lg bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden w-full flex items-center justify-between px-5 py-4 text-left"
        aria-expanded={isOpen}
      >
        <span className="font-body text-sm font-semibold text-[#2D7E32]">
          Browse {total} {total === 1 ? 'topic' : 'topics'}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#D5B13A] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div className={`${isOpen ? 'block' : 'hidden'} md:block px-5 pt-5 pb-4 md:py-5`}>
        <p className="hidden md:block font-body text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-3">
          On this page
        </p>
        <div className="space-y-5">
          {groups.map((group, gi) => (
            <div key={group.category ?? `ungrouped-${gi}`}>
              {group.category && (
                <p className="font-heading text-[11px] font-bold uppercase tracking-[0.15em] text-[#D5B13A] mb-2">
                  {group.category}
                </p>
              )}
              <ul className="space-y-1.5">
                {group.tabs.map((tab) => (
                  <li key={tab.id}>
                    <Link
                      href={`/${folderSlug}/${tab.slug}`}
                      className="group flex items-baseline gap-2 py-1 transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D5B13A]/40 group-hover:bg-[#D5B13A] transition-colors flex-shrink-0 translate-y-1.5" />
                      <span className="font-body text-sm text-gray-700 group-hover:text-[#2D7E32] transition-colors leading-snug">
                        {tab.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
